import { noop } from 'lodash-es';
import { observer } from 'mobx-react';
import qs from 'qs';
import React, { useCallback, useMemo, useRef } from 'react';
import { useHistory } from 'react-router';

import { Button } from '@mui/material';

import { emptyArray, SEARCH_TYPE_COOKIE, SearchType } from 'app/constants';
import SearchStore from 'app/store/SearchStore';
import usePersistent from 'hooks/use-persistent';
import useShortcutSearchInput from 'hooks/use-shortcut-search-input';
import useStores from 'hooks/use-stores';
import { ElasticQueryBuilder } from 'modules/elastic';
import { usePublicSubscriptionTemplates } from 'queries/subscription-templates/use-public-subscription-templates';
import { setCookie } from 'utils/cookies';
import normalizeSearch from 'utils/normalize-search';
import { qsReplacer } from 'utils/qs-replacer';

import DesktopSearchField from './components/DesktopSearchField';
import MobileSearchField from './components/MobileSearchField';
import SearchTypeTabs from './components/SearchTypeTabs';
import useSuggestions from './hooks/use-suggestions';
import useStyles from './styles';
import { ChangeTrigger, SearchFieldRef, SearchFieldValue } from './types';

interface Props {
    className?: string;
    inputClassName?: string;
    listClassName?: string;
    onSubmit?: () => void;
    variant: 'desktop' | 'mobile';
}

const SearchBar: React.FC<Props> = ({
    className,
    inputClassName,
    listClassName,
    variant = 'desktop',
    onSubmit = noop
}) => {
    const { searchStore } = useStores();
    const history = useHistory();
    const { classes, cx } = useStyles();

    const fieldRef = useRef<SearchFieldRef>(null);
    const autoCompleteInputRef = useShortcutSearchInput();

    const { data: templates = emptyArray } = usePublicSubscriptionTemplates();

    const { query, software, version, type } = searchStore;
    const value: SearchFieldValue = useMemo(
        () => ({ query, software, version, type }),
        [query, software, type, version]
    );

    const persistent = usePersistent({
        value,
        onSubmit,
        templates
    });

    const updateUrl = useCallback(() => {
        const { query, software, version, type, requestState } = searchStore;

        if (window.location.pathname.startsWith('/search/tags')) {
            const template = templates.find(({ aliasUrl }) => window.location.pathname.includes(aliasUrl));
            if (template && normalizeSearch(String(template.query)) === normalizeSearch(String(query))) {
                return;
            }
        }

        const state = searchStore.hasTerms ? ElasticQueryBuilder.stateToString(requestState) : undefined;

        switch (type) {
            case SearchType.simple:
                history.push({
                    pathname: '/search',
                    search: qs.stringify({ query }, { filter: qsReplacer })
                });
                break;
            case SearchType.product:
                history.push({
                    pathname: '/search',
                    search: qs.stringify({ software, version }, { filter: qsReplacer })
                });

                break;
            case SearchType.elastic:
                history.push({
                    pathname: '/search',
                    search: qs.stringify({ state }, { filter: qsReplacer })
                });
                break;
            default:
                break;
        }
    }, [history, searchStore]);

    const refetch = useCallback(() => {
        const { query, software, version, type, requestState } = searchStore;
        const parameters = { query, software, version, type };
        const state = type === SearchType.elastic ? { ...requestState, searchTerm: query } : requestState;

        searchStore.setParameters({ ...parameters, state });
        setTimeout(() => updateUrl(), 0);
        searchStore.performFetch();
    }, [searchStore, updateUrl]);

    const handleSubmit = useCallback(() => {
        refetch();
        persistent.current.onSubmit();
    }, [persistent, refetch]);

    const handleChange = useCallback(
        (payload: SearchFieldValue, trigger: ChangeTrigger) => {
            if (payload.type === searchStore.type) {
                searchStore.setParameters({
                    ...payload,
                    state: payload.type === SearchType.elastic ? searchStore.requestState : {}
                });
            } else {
                searchStore.setPageSize(SearchStore.DEFAULT_PAGE_SIZE);
                searchStore.setParameters({ ...payload, state: {} });
            }

            setCookie(SEARCH_TYPE_COOKIE, payload.type, { ['max-age']: Number.MAX_SAFE_INTEGER });

            if (trigger === ChangeTrigger.select) {
                refetch();
            }
        },
        [refetch, searchStore]
    );

    const handleSearchTypeTabsChange = useCallback(
        (type: SearchType) => {
            handleChange({ ...persistent.current.value, type }, ChangeTrigger.select);
        },
        [handleChange, persistent]
    );

    const { suggestions, isLoading } = useSuggestions(value);

    return variant === 'desktop' ? (
        <DesktopSearchField
            inputClassName={inputClassName}
            listClassName={listClassName}
            autoCompleteInputRef={autoCompleteInputRef}
            className={className}
            value={value}
            onSubmit={handleSubmit}
            onChange={handleChange}
            onInputFieldEnter={handleSubmit}
            onOptionClick={onSubmit}
            suggestions={suggestions}
            loading={isLoading}
        />
    ) : variant === 'mobile' ? (
        <div className={cx(classes.container, className)}>
            <MobileSearchField
                ref={fieldRef}
                inputClassName={inputClassName}
                listClassName={listClassName}
                autoCompleteInputRef={autoCompleteInputRef}
                value={value}
                onSubmit={handleSubmit}
                onChange={handleChange}
                onInputFieldEnter={handleSubmit}
                onOptionClick={onSubmit}
                suggestions={suggestions}
                loading={isLoading}
            />
            <div className={classes.toolbar}>
                <Button
                    variant="contained"
                    fullWidth
                    disableElevation
                    disableFocusRipple
                    centerRipple
                    onClick={() => fieldRef.current?.submit()}
                    className={classes.search}
                >
                    {'Search'}
                </Button>
                <SearchTypeTabs value={value.type} onChange={handleSearchTypeTabsChange} />
            </div>
        </div>
    ) : null;
};

export default observer(SearchBar);
